import useGetLineItem from '@/api/queries/lineItems/useGetLineItem';
import { ContextMenuDropdown, SkeletonLoader } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import MarkdownCore from '@payaca/components/markdownLabel/MarkdownCore';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useEffect } from 'react';

export type LinkedItemPrice = {
  value: number | null;
  currency: { code: string; exponent: number } | null;
};

const LinkedItemRowSkeleton: FC = () => (
  <div className="-mx-4 flex items-center gap-3 border-t border-gray-100 px-4 py-3 last:border-b md:-mx-7 md:px-7">
    <SkeletonLoader className="h-9 w-9 shrink-0 rounded-md" />
    <span className="min-w-0 flex-1">
      <SkeletonLoader className="h-4 w-40" />
    </span>
    <SkeletonLoader className="h-4 w-16 shrink-0" />
    <SkeletonLoader className="h-7 w-7 shrink-0 rounded-md" />
  </div>
);

/**
 * A linked Item row. It owns its own GetLineItem fetch — React Query is the
 * single source of truth — and reports its price up so the parent can total
 * the Items without shadowing the cache in its own state.
 */
const LinkedItemRow: FC<{
  lineItemId: string;
  onPriceChange: (lineItemId: string, price: LinkedItemPrice) => void;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ lineItemId, onPriceChange, onEdit, onRemove }) => {
  const { data, isError } = useGetLineItem({ lineItemId });
  const loaded = data?.lineItem;
  // Once the fetch settles (success or error) stop showing the skeleton, so a
  // failed/empty GetLineItem falls back to a label rather than loading forever.
  const isPending = !loaded && !isError;

  // `primary` only backs the accessible row / actions-menu label. The visible
  // layout mirrors the Estimate Builder: the internal reference above the
  // (markdown) description.
  const description = loaded?.description?.trim() || '';
  const name = loaded?.name?.trim() || '';
  const primary = description || name || `Item ${lineItemId}`;
  const priceValue = loaded?.price?.unitPrice?.value ?? null;
  const priceCurrency = loaded?.price?.unitPrice?.currency ?? null;

  useEffect(() => {
    if (!loaded) return;
    onPriceChange(lineItemId, { value: priceValue, currency: priceCurrency });
  }, [lineItemId, loaded, priceValue, priceCurrency, onPriceChange]);

  if (isPending) {
    return <LinkedItemRowSkeleton />;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        // Ignore key events bubbling up from the actions menu button.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
      aria-label={`Edit ${primary}`}
      className="-mx-4 flex cursor-pointer items-center gap-3 border-t border-gray-100 px-4 py-3 transition-colors last:border-b hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 md:-mx-7 md:px-7"
    >
      {loaded?.thumbnailUrl ? (
        <img
          src={loaded.thumbnailUrl}
          alt=""
          className="h-9 w-9 shrink-0 rounded-md border border-gray-200 object-contain"
        />
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-gray-400">
          <UntitledIcon name="box.3" className="h-4 w-4" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        {name && <h4 className="truncate">{name}</h4>}
        {description && (
          <div className="line-clamp-1 [&_p]:m-0 [&_p]:inline">
            <MarkdownCore markdown={description} />
          </div>
        )}
      </div>
      {priceValue != null && priceCurrency && (
        <span className="shrink-0 font-medium">
          <CurrencyValue value={priceValue} currency={priceCurrency} />
        </span>
      )}
      {/* Stop propagation on a wrapper, not on ContextMenuDropdown itself: HeadlessUI's
          MenuButton owns the button's onClick, so a forwarded handler doesn't
          reliably stop the click bubbling to the row's onEdit. */}
      <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
        <ContextMenuDropdown
          size="xs"
          variant="secondary"
          className="aspect-square shrink-0"
          label={`Actions for ${primary}`}
          menuItems={[
            { label: 'Edit', onClick: () => onEdit() },
            { label: 'Remove', onClick: () => onRemove() },
          ]}
        />
      </span>
    </div>
  );
};

export default LinkedItemRow;
